/* eslint-disable max-lines */
import {
    CannedMessage,
    CannedMessageKey,
    CannedMessageProps,
    SendCannedMessagesViewProps,
} from './types'
import {
    ChatAltIcon,
    ChevronLeftIcon,
    FolderIcon,
    NinchatReturnIcon,
    SelectorIcon,
    XIcon,
} from '../Icon'
import {
    getCannedMessageText, getCannedMessagesKey,
    hasChildren, isCannedMsgEqual,
    sortCannedMessagesLanguages, translate,
} from 'utils/cannedmessages'
import {
    styledActions,
    styledButton,
    styledHeader,
    styledHelpers,
    styledKeywords,
    styledList,
    styledMessage,
    styledMessageContainer,
    styledSendCannedMessagesView,
    styledShared,
    styledSmall,
    styledTitle,
} from './styles'
import {
    useAppDispatch,
    useAppSelector,
} from '../../hooks'
import Button from 'components/Button'
import React from 'react'
import Select from 'components/Form/Select'

import {styled} from 'stitches/config'
import {updateCannedMessageKey} from 'state/ui'
import {useRealm} from 'hooks/ninchat'
import {useRovingTabIndex} from '../../utils/RovingTabIndex'
import {useTranslation} from 'react-i18next'

interface StateProps {
    cannedMessages: CannedMessage[]
    selectedCannedMessages: CannedMessage[]
}

const StyledSendCannedMessagesView = styled('div', styledSendCannedMessagesView)
const StyledHeader = styled('div', styledHeader)
const StyledTitle = styled('div', styledTitle)
const StyledLanguageSelectSpacer = styled('span', {})
const StyledHelpers = styled('div', styledHelpers)
const StyledSmall = styled('small', styledSmall)
const StyledShared = styled('small', styledShared)
const StyledActions = styled('div', styledActions)
const StyledList = styled('div', styledList)
const StyledButton = styled('button', styledButton)
const StyledMessageContainer = styled('div', styledMessageContainer)
const StyledKeywords = styled('div', styledKeywords)
const StyledMessage = styled('div', styledMessage)

const SendCannedMessagesView = (props: SendCannedMessagesViewProps): React.ReactElement => {
    const {
        cannedMessages,
        supportedCannedMessagesLanguages,
        filter,
        onClose,
        onSelect,
        realmId,
    } = props

    const canned_messages_search_keywords_only = useAppSelector(({ninchat}) => ninchat.user_settings.canned_messages_search_keywords_only ?? false)
    const realmName = useRealm(realmId)?.realm_attrs.name

    const {t} = useTranslation()

    const dispatch = useAppDispatch()

    const cannedMessageKey = useAppSelector(({ui}) => getCannedMessagesKey(supportedCannedMessagesLanguages, ui?.canned_message_key))
    const sortedSupportedCannedMessagesLanguages = sortCannedMessagesLanguages(supportedCannedMessagesLanguages, t, cannedMessageKey)

    React.useEffect(() => {
        const onKeyup = (e: KeyboardEvent): void => {
            switch (e.key) {
                case 'Escape':
                    onClose()
                    break
                default:
                    break
            }
        }

        window.addEventListener('keyup', onKeyup)

        return () => window.removeEventListener('keyup', onKeyup)
    }, [])

    const defaultState: StateProps = {
        cannedMessages: [],
        selectedCannedMessages: [],
    }

    const [state, setState] = React.useState(defaultState)

    const listRef = useRovingTabIndex({
        filter: Boolean(filter),
        nodes: state.cannedMessages,
    })

    const filterCannedMessages = (selectedCannedMessage: CannedMessage): CannedMessage[] => {
        // Always include messages that have children; for others we keep the old rule
        const _cannedMessages = cannedMessageKey
            ? cannedMessages
                .filter((m: CannedMessageProps) => getCannedMessageText(m, cannedMessageKey) || hasChildren(m, cannedMessages, cannedMessageKey))
            : cannedMessages

        if (selectedCannedMessage) {
            const filtered = _cannedMessages.reduce((cms: CannedMessage[], fm: CannedMessageProps) => {
                const cm = {...fm} as CannedMessage
                const isSelected = isCannedMsgEqual(cm, selectedCannedMessage)

                cm.hasChildren = !isSelected && hasChildren(cm, _cannedMessages, cannedMessageKey)

                // Hide if canned message doesn't have children nor message (only parents can have empty messages)
                const hide = !cm.hasChildren && !getCannedMessageText(cm, cannedMessageKey)

                // Check if keyword begins with selected keyword and that the rest of the keyword does not contain "/",
                // hence making it a direct descendant of the selected message
                if (cm.keywords[0].indexOf(selectedCannedMessage.keywords[0]) === 0 &&
                    // cm.keywords[0] !== selectedCannedMessage.keywords[0] &&
                    cm.keywords[0].replace(`${selectedCannedMessage.keywords[0]}/`, '').indexOf('/') === -1 && !hide) {
                    return cms.concat(cm)
                }

                return cms
            }, [])

            // Check if selected message exists at first index
            // Selected message may not exist if it doesn't have message property
            if (filtered[0]?.keywords[0] === selectedCannedMessage.keywords[0]) {
            // TODO: Replace this with Array.findLastIndex when ES compatibility is updated
                let lastWithChildrenIndex = 0
                filtered.map((cm: CannedMessage, i) => {
                    if (cm.hasChildren) {
                        lastWithChildrenIndex = i
                    }

                    return cm
                })

                // Move selected message from index 0 to after last message with children
                if (lastWithChildrenIndex > 0) {
                    const [selectedMessage] = filtered.splice(0, 1)
                    filtered.splice(lastWithChildrenIndex, 0, selectedMessage)
                }
            }

            return filtered
        } else if (filter) {
            const keyword = filter.substring(1).toLowerCase()

            if (canned_messages_search_keywords_only) {
                // Remove "/" from the filter and see if filter string is part of the keyword
                return _cannedMessages.filter((fm: CannedMessageProps) => getCannedMessageText(fm, cannedMessageKey) && fm.keywords[0].toLowerCase()
                    .indexOf(keyword) !== -1)
            }

            // Remove "/" from the filter and see if filter string is part of the keyword or message
            return _cannedMessages.filter((fm: CannedMessageProps) => getCannedMessageText(fm, cannedMessageKey) && (fm.keywords[0].toLowerCase()
                .indexOf(keyword) !== -1 || getCannedMessageText(fm, cannedMessageKey)?.toLowerCase()
                .indexOf(keyword) !== -1))
        }

        return _cannedMessages.reduce((cms: CannedMessage[], fm: CannedMessageProps) => {
            // If keyword has "/" it can't be on the 1st level
            if (fm.keywords[0].indexOf('/') === -1) {
                const cm = {...fm} as CannedMessage
                cm.hasChildren = hasChildren(cm, _cannedMessages, cannedMessageKey)

                // Skip if canned message doesn't have children nor message (only parents can have empty messages)
                if (!cm.hasChildren && !getCannedMessageText(cm, cannedMessageKey)) {
                    return cms
                }

                return cms.concat(cm)
            }

            return cms
        }, [])
    }

    const updateState = (newState: StateProps): void => {
        const selectedCannedMessage = newState.selectedCannedMessages[newState.selectedCannedMessages.length - 1] || null
        const filteredCannedMessages = filterCannedMessages(selectedCannedMessage)

        const updatedState: StateProps = {
            ...newState,
            cannedMessages: filteredCannedMessages,
        }

        setState(updatedState)
    }

    React.useEffect(() => {
        const newState: StateProps = {...state}

        updateState(newState)
    }, [cannedMessages, filter])

    const onBack = (): void => {
        const newState: StateProps = {...state}

        newState.selectedCannedMessages.pop()

        updateState(newState)
    }

    const onClick = (m: CannedMessage): void => {
        if (m.hasChildren) {
            const newState: StateProps = {...state}

            newState.selectedCannedMessages.push(m)

            updateState(newState)
        } else {
            const messageText = getCannedMessageText(m, cannedMessageKey)
            const cm: CannedMessageProps = {
                ...m,
                keywords: m.keywords,
                message: messageText,
            }

            onSelect(cm)
        }
    }

    const onSelectLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(updateCannedMessageKey(e.target.value as CannedMessageKey))
    }

    const renderCannedMessage = (m: CannedMessage): React.ReactElement => {
        const messageText = getCannedMessageText(m, cannedMessageKey)
        return (
            <StyledButton
                key={`${m.shared ? 'org' : 'own'}-${m.keywords[0]}`}
                onClick={(): void => onClick(m)}
                tabIndex={-1}
                type='button'
            >
                <StyledMessageContainer>
                    <StyledKeywords>/{m.keywords[0]} {!!m.shared && !!realmName && <StyledShared>({realmName})</StyledShared>}</StyledKeywords>
                    {!m.hasChildren && <StyledMessage>{messageText}</StyledMessage>}
                </StyledMessageContainer>
                {!!m.hasChildren && <FolderIcon />}
            </StyledButton>
        )
    }

    return (
        <StyledSendCannedMessagesView>
            <StyledHeader>
                <StyledTitle>
                    <ChatAltIcon /> {t('Canned messages')}
                    <StyledLanguageSelectSpacer>
                        <Select
                            compact={true}
                            css={{
                                maxWidth: '100%',
                                minWidth: 0,
                                width: 'fit-content',
                            }}
                            onChange={onSelectLanguage}
                            title={t('Language')}
                            value={cannedMessageKey}
                        >
                            {sortedSupportedCannedMessagesLanguages.map((lang) => (
                                <option key={lang.id} value={lang.id}>
                                    {translate(lang.name, t)}
                                </option>
                            ))}
                        </Select>
                    </StyledLanguageSelectSpacer>
                </StyledTitle>
                <StyledHelpers>
                    <StyledSmall><SelectorIcon /> {t('navigate')}</StyledSmall>
                    <StyledSmall><NinchatReturnIcon />{t('select')}</StyledSmall>
                    <StyledSmall><i>esc</i> {t('exit')}</StyledSmall>
                </StyledHelpers>
                <StyledActions>
                    <Button color='text' iconLeft={XIcon} onPress={onClose} size='small' title={t('Close canned messages')} />
                </StyledActions>
            </StyledHeader>
            <StyledList ref={listRef}>
                {state.selectedCannedMessages.length > 0 &&
                    <StyledButton
                        onClick={(): void => onBack()}
                        tabIndex={-1}
                        type='button'
                    >
                        <ChevronLeftIcon /> {t('Back')}
                    </StyledButton>}
                {state.cannedMessages.map((message) => renderCannedMessage(message))}
            </StyledList>
        </StyledSendCannedMessagesView>
    )
}

SendCannedMessagesView.defaultProps = {
    filter: null,
    realmId: undefined,
}

export default SendCannedMessagesView
